// ═══════════════════════════════════════════════════════════════════════════
// ForgeTrack Context Help — API Hook
// apps/web/src/lib/api/context-help.ts
//
// Fetches all context help items for a locale in a single call.
// Cached in React state — help data changes infrequently.
// ═══════════════════════════════════════════════════════════════════════════

import { useState, useEffect, useCallback } from 'react';
import { useApiClient } from '@forgetrack/api-client';

// ─── Types ───────────────────────────────────────────────────────────────────

export interface ContextHelpItem {
  helpKey: string;
  title: string;
  body: string;
  category: string;
  learnMoreUrl: string | null;
  relatedKeys: string | null;
}

export interface ContextHelpBulkResponse {
  locale: string;
  count: number;
  items: ContextHelpItem[];
}

// ─── Hook ────────────────────────────────────────────────────────────────────

export function useContextHelpData(locale: string = 'en', enabled: boolean = true) {
  const { createApi } = useApiClient();
  const [data, setData] = useState<Map<string, ContextHelpItem>>(new Map());
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);

  const fetch = useCallback(async () => {
    if (!enabled) return;
    try {
      setLoading(true);
      const api = await createApi();
      const response = await api.get<ContextHelpBulkResponse>(
        `/api/v1/context-help?locale=${encodeURIComponent(locale)}`
      );
      const map = new Map<string, ContextHelpItem>();
      for (const item of response.data.items) {
        map.set(item.helpKey, item);
      }
      setData(map);
      setLoaded(true);
    } catch (err) {
      console.warn('[ContextHelp] Failed to load help data:', err);
      // Non-fatal — help tooltips just won't appear
    } finally {
      setLoading(false);
    }
  }, [createApi, locale, enabled]);

  useEffect(() => {
    if (enabled && !loaded) {
      fetch();
    }
  }, [enabled, loaded, fetch]);

  const getHelp = useCallback(
    (helpKey: string): ContextHelpItem | undefined => data.get(helpKey),
    [data]
  );

  const getRelated = useCallback(
    (helpKey: string): ContextHelpItem[] => {
      const item = data.get(helpKey);
      if (!item?.relatedKeys) return [];
      return item.relatedKeys
        .split(',')
        .map(k => k.trim())
        .map(k => data.get(k))
        .filter((x): x is ContextHelpItem => !!x);
    },
    [data]
  );

  return { data, loading, loaded, getHelp, getRelated, refetch: fetch };
}
